<template>
    <v-container class="pa-5" fluid>
        <page-title>{{ pageTitle }}</page-title>

        <v-row>
            <v-col cols="12" xl="10" offset-xl="1">
                <v-container>
                    <v-row>
                        <v-col cols="auto">
                            <h2 class="text-h6">
                                Parametry produktu
                            </h2>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-card>
                                <v-container>
                                    <v-row align="center">
                                        <v-col cols="12" md="4">
                                            <v-autocomplete
                                                v-if="!isEditMode"
                                                v-model="productType"
                                                :items="products"
                                                label="Rodzaj produktu"
                                                item-text="productType.name"
                                                return-object
                                                required
                                                outlined
                                                dense
                                                :error-messages="productProductTypeErrors()"
                                                @change="getProductComponents"
                                            />
                                            <v-text-field
                                                v-else
                                                :value="product.productType.name"
                                                label="Rodzaj produktu"
                                                disabled
                                                outlined
                                                dense
                                            />
                                        </v-col>
                                        <v-col cols="12" md="4">
                                            <v-text-field
                                                v-model="product.name"
                                                label="Nazwa produktu"
                                                required
                                                outlined
                                                dense
                                                :disabled="!hasProductType"
                                                :readonly="!isCalculationEditable"
                                                :error-messages="productNameErrors()"
                                            />
                                        </v-col>
                                        <v-col cols="12" md="2">
                                            <v-text-field
                                                :value="product.formatA"
                                                label="Szerokość produktu"
                                                suffix="mm"
                                                outlined
                                                dense
                                                :disabled="!hasProductType"
                                                :readonly="!isCalculationEditable"
                                                :error-messages="productFormatAErrors()"
                                                @input="changeFormatA"
                                            />
                                        </v-col>
                                        <v-col cols="12" md="2">
                                            <v-text-field
                                                :value="product.formatB"
                                                label="Wysokość produktu"
                                                suffix="mm"
                                                outlined
                                                dense
                                                :disabled="!hasProductType"
                                                :readonly="!isCalculationEditable"
                                                :error-messages="productFormatBErrors()"
                                                @input="changeFormatB"
                                            />
                                        </v-col>
                                    </v-row>
                                    <v-row>
                                        <v-col cols="12" md="2">
                                            <v-text-field
                                                v-model="product.amount"
                                                v-mask="'#######'"
                                                label="Nakład"
                                                required
                                                outlined
                                                dense
                                                :disabled="!hasProductType"
                                                :readonly="!isCalculationEditable"
                                                :error-messages="productAmountErrors()"
                                            />
                                        </v-col>
                                    </v-row>
                                </v-container>
                            </v-card>
                        </v-col>
                    </v-row>

                    <v-row align="center">
                        <v-col cols="auto">
                            <h2 class="text-h6">
                                Komponenty
                            </h2>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col v-if="!hasProductType" cols="12">
                            <v-card>
                                <v-card-text>
                                    <v-alert
                                        type="info"
                                        text
                                        border="left"
                                    >
                                        Produkt nie posiada przypisanych komponentów.
                                    </v-alert>
                                </v-card-text>
                            </v-card>
                        </v-col>

                        <v-col v-for="(component, componentIndex) in product.calculationProductComponents" v-else :key="component.id" cols="12">
                            <v-card>
                                <v-card-title>
                                    <v-row align="center">
                                        <v-col>
                                            <div class="text-h6 text-no-wrap">
                                                {{ component.component.name }} ({{ component.component.code }})
                                            </div>
                                        </v-col>
                                    </v-row>
                                </v-card-title>

                                <v-card-text>
                                    <v-alert
                                        v-if="component.calculationProductComponentProperties.length === 0"
                                        type="info"
                                        text
                                        border="left"
                                    >
                                        Brak przypisanych cech produkcyjnych.
                                    </v-alert>
                                    <v-row v-else>
                                        <v-col cols="12">
                                            <strong>Cechy produkcyjne:</strong>
                                        </v-col>
                                        <v-col
                                            v-for="(productionProperty, propertyIndex) in component.calculationProductComponentProperties"
                                            :key="productionProperty.productProductionPropertyId"
                                            cols="12"
                                            sm="4"
                                            lg="3"
                                        >
                                            <v-autocomplete
                                                v-if="productionProperty.productProductionProperty.name === 'rodzaj_papieru'"
                                                v-model="productionProperty.value"
                                                :items="papers"
                                                :filter="customFilter"
                                                :label="productionProperty.productProductionProperty.verboseName | capitalize"
                                                item-value="name"
                                                item-text="name"
                                                no-data-text="Nie znaleziono"
                                                outlined
                                                dense
                                                :readonly="!isCalculationEditable"
                                                :error-messages="productionPropertyValueErrors(componentIndex, propertyIndex)"
                                            />
                                            <v-text-field
                                                v-else
                                                v-model="productionProperty.value"
                                                :label="productionProperty.productProductionProperty.verboseName | capitalize"
                                                outlined
                                                dense
                                                :readonly="!isCalculationEditable"
                                                :error-messages="productionPropertyValueErrors(componentIndex, propertyIndex)"
                                                @input="changePropertyValue(productionProperty)"
                                            />
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12">
                            <v-btn
                                color="error"
                                medium
                                class="mr-3"
                                :disabled="actionLoading"
                                data-sentry-label="Wstecz"
                                @click="close"
                            >
                                Wstecz
                            </v-btn>
                            <v-btn
                                v-if="isCalculationEditable"
                                color="success"
                                medium
                                :loading="actionLoading"
                                data-sentry-label="Zapisz"
                                @click="saveProduct"
                            >
                                Zapisz
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import store from '@/./store'

import PageTitle from '@/components/common/PageTitle'

import tooltipDelay from '@/mixins/tooltip'
import customFilters from '@/mixins/customFilters'
import parse from '@/mixins/parse'

import { errorHandler } from '@/utils/errorHandler'

import { uniqBy, filter } from 'lodash'
import { required, integer, minValue } from 'vuelidate/lib/validators'
import { mask } from 'vue-the-mask'


export default {
    name: 'Product',
    components: {
        PageTitle
    },
    directives: { mask },
    mixins: [
        tooltipDelay,
        customFilters,
        parse
    ],
    beforeRouteEnter(to, from, next) {
        if (from.name) {
            Promise.all([
                store.dispatch('calculations/getProducts', {
                    shouldSavedInStore: false
                }),
                store.dispatch('configurations/getPapers', {
                    shouldSavedInStore: false
                })
            ])
                .then(responses => {
                    next(vm => {
                        const calculationProducts = JSON.parse(JSON.stringify(
                            store.state.calculations.calculations.calculation.calculationProducts
                        ))

                        vm.products = responses[0].data.results
                        vm.papers = responses[1].data.results
                        vm.isCalculationEditable = store.state.calculations.calculations.calculation.statusId === 10 // Nowa

                        vm.pageTitle = to.meta.title

                        if (typeof to.params.productId !== 'undefined') { // Product edit
                            vm.actionName = 'updateCalculationProduct'
                            vm.product = calculationProducts.find(item => item.id == to.params.productId)
                            vm.savedProductName = vm.product.name
                        } else {
                            vm.actionName = 'addCalculationProduct'
                        }

                        vm.calculationProductsNames = calculationProducts.map(item => item.name)
                    })
                })
                .catch(error => {
                    next(error)
                    errorHandler(error)
                })
        } else {
            next({
                name: 'calculationsCalculation',
                params: {
                    id: to.params.id
                }
            })
        }
    },
    data() {
        return {
            isCalculationEditable: null,
            actionName: null,
            pageTitle: '',
            productType: null,
            product: {
                name: null,
                productTypeId: null,
                productType: null,
                amount: null,
                numberOfVersions: 1, // Temporary hardcoded
                calculationProductComponents: [],
                formatA: null,
                formatB: null,
            },
            savedProductName: null,
            products: [],
            papers: [],
            calculationProductsNames: [],
            actionLoading: false
        }
    },
    computed: {
        isEditMode() {
            return this.actionName === 'updateCalculationProduct'
        },
        hasProductType() {
            return this.product.calculationProductComponents.length > 0
        }
    },
    validations() {
        const rules = {
            product: {
                name: {
                    required,
                    isUnique: value => {
                        const trimmedValue = value?.trimStart().trimEnd()

                        return trimmedValue === this.savedProductName || !this.calculationProductsNames.some(item => item === trimmedValue)
                    }
                },
                amount: {
                    required,
                    minValue: minValue(1)
                },
                calculationProductComponents: {
                    $each: {
                        calculationProductComponentProperties: {
                            $each: {
                                value: {
                                    required
                                }
                            }
                        }
                    }
                }
            },
        }

        if (!this.isEditMode) {
            rules.productType = {
                required
            }
        }

        if (this.product.formatA) {
            rules.product['formatA'] = {
                integer
            }
        }

        if (this.product.formatB) {
            rules.product['formatB'] = {
                integer
            }
        }

        return rules
    },
    methods: {
        getProductComponents(product) {
            this.product.productTypeId = product.productType.id
            this.product.productType = product.productType
            this.product.calculationProductComponents = []

            this.$store.dispatch('calculations/getProductComponents', product.id)
                .then(response => {
                    const calculationProductProductionPropertyProductTypes = response.data.calculationProductProductionPropertyProductTypes,
                        calculationProductionProcessProductTypes = response.data.calculationProductionProcessProductTypes,
                        allRequiredProperties = filter(calculationProductProductionPropertyProductTypes, { 'isRequired': true }),
                        components = uniqBy(allRequiredProperties.map(item => ({
                            component: item.component,
                            componentId: item.componentId,
                        })), product.id)

                    components.forEach(component => {
                        const calculationProductComponentProperties = filter(allRequiredProperties, item => item.component.id === component.componentId)
                            .map(item => ({
                                productProductionProperty: item.productProductionProperty,
                                productProductionPropertyId: item.productProductionProperty.id,
                                value: item.productProductionProperty.defaultValue || null
                            }))

                        const calculationComponentProcesses = filter(calculationProductionProcessProductTypes, item => item.component.id === component.componentId)
                            .map(item => ({
                                productionProcess: item.productionProcess,
                                productionProcessId: item.productionProcess.id
                            }))

                        this.product.calculationProductComponents.push({
                            component: component.component,
                            componentId: component.componentId,
                            calculationProductComponentProperties: calculationProductComponentProperties,
                            calculationComponentProcesses: calculationComponentProcesses,
                            parts: 1
                        })
                    })
                })
                .catch(error => errorHandler(error))
        },
        changeFormatA(value) {
            this.product.formatA = this.parseEmptyStringToNull(value)
            const propertyIndex = this.product.calculationProductComponents[0].calculationProductComponentProperties.findIndex(item => item.productProductionPropertyId === 5)

            this.product.calculationProductComponents[0].calculationProductComponentProperties[propertyIndex].value = this.product.formatA
        },
        changeFormatB(value) {
            this.product.formatB = this.parseEmptyStringToNull(value)
            const propertyIndex = this.product.calculationProductComponents[0].calculationProductComponentProperties.findIndex(item => item.productProductionPropertyId === 6)

            this.product.calculationProductComponents[0].calculationProductComponentProperties[propertyIndex].value = this.product.formatB
        },
        changePropertyValue(productionProperty) {
            if (productionProperty.productProductionPropertyId === 5) { // format_x
                this.product.formatA = productionProperty.value
            }

            if (productionProperty.productProductionPropertyId === 6) { // format_y
                this.product.formatB = productionProperty.value
            }
        },
        saveProduct() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })
            } else {
                this.actionLoading = true

                this.$store.dispatch(`calculations/${this.actionName}`, this.product)

                this.$store.dispatch('calculations/updateCalculation', this.$store.state.calculations.calculations.calculation)
                    .then(() => {
                        this.close()
                    })
                    .catch(error => errorHandler(error))
                    .finally(() => {
                        this.actionLoading = false
                    })
            }
        },
        close() {
            this.$router.push({
                name: 'calculationsCalculation',
                params: {
                    id: this.$store.state.calculations.calculations.calculation.id,
                    calculationNumber: this.$store.state.calculations.calculations.calculation.number,
                }
            })
                .catch(error => errorHandler(error))
        },

        // Walidacje
        productNameErrors() {
            const errors = []

            if (!this.$v.product.name.$dirty) return errors
            !this.$v.product.name.required && errors.push('Proszę podać nazwę produktu.')
            !this.$v.product.name.isUnique && errors.push('Kalkulacja zawiera już produkt o takiej nazwie.')

            return errors
        },
        productProductTypeErrors() {
            const errors = []

            if (!this.$v.productType.$dirty) return errors
            !this.$v.productType.required && errors.push('Proszę podać rodzaj produktu.')

            return errors
        },
        productFormatAErrors() {
            if (this.product.formatA) {
                const errors = []

                if (!this.$v.product.formatA.$dirty) return errors
                this.$v.product.formatA.$invalid && errors.push('Nieprawidłowa wartość.')

                return errors
            }
        },
        productFormatBErrors() {
            if (this.product.formatB) {
                const errors = []

                if (!this.$v.product.formatB.$dirty) return errors
                this.$v.product.formatB.$invalid && errors.push('Nieprawidłowa wartość.')

                return errors
            }
        },
        productAmountErrors() {
            const errors = []

            if (!this.$v.product.amount.$dirty) return errors

            if (!this.$v.product.amount.required || !this.$v.product.amount.minValue) {
                errors.push('Proszę podać nakład.')
            }

            return errors
        },
        productionPropertyValueErrors(componentIndex, propertyIndex) {
            const errors = []

            if (!this.$v.product.calculationProductComponents.$each[componentIndex].calculationProductComponentProperties.$each[propertyIndex].$dirty) return errors

            !this.$v.product.calculationProductComponents.$each[componentIndex].calculationProductComponentProperties.$each[propertyIndex].value.required && errors.push('Proszę podać wartość.')

            return errors
        },
    }
}
</script>
